import { Entity } from '@api/api/entity-schema';
import { formatTicker } from '@api/mappers/formatTicker';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { useFilterActions } from '@terminal/hooks/use-filter-actions/useFilterActions';
import { useEarningsData } from '@terminal/react-query-hooks/earnings/useEarningsData';
import { queryKeys } from '@terminal/routes/query-params';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { EarningsContent } from './components/earnings-content/EarningsContent';
import { RevampEarningsContent } from './components/revamp-earnings-content/RevampEarningsContent';
import styles from './Earnings.module.scss';
import { useEarningsStore } from './useEarningsStore/useEarningsStore';
import { getEarningsFilterPayload, getEarningsFilters } from './utils/utils';

export type EarningsProps = {
  entity: Entity;
};

export const Earnings = ({ entity }: EarningsProps) => {
  const handledEpsFilterParamsRef = useRef<string | null>(null);

  const isNewEarningsTabEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4671-ao-earnings')
  );

  const { isFetching, data } = useEarningsData(entity.tag);
  const { t } = useTranslation([
    'widget',
    'asset-overview',
    'chat',
    'common',
    'screener',
    'scenario',
    'earnings',
    'analyze',
    'stream',
  ]);
  const {
    initStore,
    setIsViewSelectedOnly,
    earningsData,
    onFilterGroupsChange,
    resetAllFilterOptions,
    selectRowsByEpisodeDates,
  } = useEarningsStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const { addFilterOption } = useFilterActions({
    store: useEarningsStore,
    group: 'EARNINGS_FILTERS',
    onFilterGroupsChange,
  });

  const episodeDates = isNewEarningsTabEnabled
    ? searchParams.get(queryKeys.episodeDates)
    : null;

  useEffect(() => {
    if (data && data.earnings !== earningsData) {
      initStore({
        data: data.earnings,
        earningExchange: data.earningExchange,
        filters: getEarningsFilters({
          isNewEarningsTabEnabled,
        }),
      });
    }
  }, [data, earningsData]);

  useEffect(() => {
    const epsFilter = searchParams.get(queryKeys.epsFilter);

    if (!epsFilter || !data || data.earnings !== earningsData) {
      handledEpsFilterParamsRef.current = null;
      return;
    }

    const epsValue = searchParams.get(queryKeys.epsValue);
    const epsFilterParamsKey = [epsFilter, epsValue, episodeDates].join(':');

    if (handledEpsFilterParamsRef.current !== epsFilterParamsKey) {
      const filterPayload = getEarningsFilterPayload(
        epsFilter,
        epsValue ? Number(epsValue) : undefined
      );

      if (filterPayload) {
        handledEpsFilterParamsRef.current = epsFilterParamsKey;
        resetAllFilterOptions();
        addFilterOption(filterPayload);

        if (episodeDates) {
          selectRowsByEpisodeDates(episodeDates.split(','));
        }

        setIsViewSelectedOnly(isNewEarningsTabEnabled);

        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete(queryKeys.epsFilter);
        nextSearchParams.delete(queryKeys.epsValue);
        nextSearchParams.delete(queryKeys.episodeDates);
        setSearchParams(nextSearchParams, { replace: true });
      }
    }
  }, [
    searchParams,
    data,
    earningsData,
    isNewEarningsTabEnabled,
    episodeDates,
    selectRowsByEpisodeDates,
  ]);

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: 'var(--content-max-width)',
              borderRadius: '8px',
              height: '360px',
              margin: '20px auto',
            },
          },
        ]}
        data-testid="earnings-loading"
      />
    );
  }

  const latest = data?.earnings?.[0];
  const hasEarnings = !!earningsData?.length;

  if (!hasEarnings) {
    return (
      <div className={styles.emptyRoot} key={entity.tag}>
        <ErrorMessageWrapper
          variant="empty"
          displayIcon
          title={t('widget:earnings.empty.title')}
          subTitle={t('widget:earnings.empty.subTitle', {
            asset: formatTicker(entity),
          })}
          customIcon={
            <img
              alt=""
              height={160}
              src="/app/static/images/empty-state/radio-telescope.png"
              width={160}
            />
          }
        />
      </div>
    );
  }

  if (isNewEarningsTabEnabled) {
    return <RevampEarningsContent entity={entity} key={entity.tag} />;
  }

  return <EarningsContent entity={entity} latest={latest} />;
};
